// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { NullableString, isString } from './string';

// Represents a boolean or null.
export type NullableBoolean = boolean | null | undefined;

// Compares two nullable booleans.
// Null or undefined value is treated as false.
export function compareBooleans(a: NullableBoolean, b: NullableBoolean): number {
    return (a === null || a === undefined || !a ? 0 : 1) - (b === null || b === undefined || !b ? 0 : 1);
}

const onRegex = /^(1|on|yes|true|enable|enabled|active)$/i;
const offRegex = /^(0|off|no|false|disable|disabled|inactive)$/i;

// Attempts to parse a boolean value, allowing for various string representations of true and false.
// If the value does not match any of the options, returns undefined.
export function tryParseSwitch(value: NullableString): boolean | undefined {
    if (!isString(value) || value.length === 0) {
        return undefined;
    } else if (onRegex.test(value)) {
        return true;
    } else if (offRegex.test(value)) {
        return false;
    } else {
        return undefined;
    }
}

// Attempts to parse a boolean value.
// Returns undefined if the value is not a string and does not match 'true' or 'false'.
export function tryParseBoolean(value: NullableString): boolean | undefined {
    if (!isString(value) || value.length === 0) {
        return undefined;
    } else {
        const valueLower = value.toLowerCase();
        return valueLower === 'true' ? true : valueLower === 'false' ? false : undefined;
    }
}
